import { useMemo } from "react";
import { useAppSelector } from "./hooks";
import { RoleIndex } from "../models/role";
import { UserType } from "../models/userType";
import { Program, clientUserPicksContext } from "../models/program";
import {
  PROGRAM_NAV,
  SALES_REP_NAV,
  SYSTEM_NAV,
  SYSTEM_ROLES,
  type NavGroup,
  type ShellArchetype
} from "../models/navigation";

export interface NavConfig {
  archetype: ShellArchetype;
  /** Rail groups, already filtered for this user. */
  groups: NavGroup[];
  /** Programs the user can jump to without opening the chooser. */
  otherPrograms: { programIndex: number; label: string }[];
  /** false for client users: the context chip renders as a label, not a button. */
  canSwitchContext: boolean;
  showSystemManagement: boolean;
  /** Which rail item to select on first paint. */
  defaultItemId: string;
}

const filterItems = (group: NavGroup, roleIndex: number, userTypeIndex?: number): NavGroup => ({
  ...group,
  items: group.items.filter(
    (item) =>
      (!item.roles || item.roles.includes(roleIndex)) &&
      (!item.userTypes || (userTypeIndex !== undefined && item.userTypes.includes(userTypeIndex)))
  )
});

/**
 * Single source of truth for what the rail contains. Replaces the branching in
 * dashboardView.tsx / systemManagementView.tsx — those files render
 * <AppShell> and read the selected item id.
 */
export const useNavConfig = (opts?: { system?: boolean }): NavConfig => {
  const { user } = useAppSelector((state: any) => state.authentication);
  const { selectedProgram, selectedUserType, programs } = useAppSelector(
    (state: any) => state.portalManagement
  );

  const roleIndex: number = user?.role_index;
  // Most client users are pinned to one context and never pass through the
  // chooser (a Manager does — clientUserPicksContext), so selectedUserType is
  // often unset for them — fall back to their own type name.
  const userTypeIndex: number | undefined =
    selectedUserType?.client_user_type_index ??
    UserType.IndexByName[user?.client_type_name as keyof typeof UserType.IndexByName];
  // Same for the program: infer it from the user's type so a client user's rail
  // is not empty. Program.AllowedUserTypes already maps type name -> program.
  const inferredProgramIndex = Number(
    Object.keys(Program.AllowedUserTypes).find((p) =>
      (Program.AllowedUserTypes[Number(p) as keyof typeof Program.AllowedUserTypes] as readonly string[])
        .includes(user?.client_type_name)
    )
  );
  const programIndex: number =
    selectedProgram?.program_index ??
    (Number.isNaN(inferredProgramIndex) ? undefined : inferredProgramIndex);

  return useMemo<NavConfig>(() => {
    const isClientUser = roleIndex === RoleIndex.ClientUser;
    const isSalesRep = userTypeIndex === UserType.Index.SalesRep;
    const showSystemManagement = SYSTEM_ROLES.includes(roleIndex);

    if (opts?.system) {
      const group = filterItems(SYSTEM_NAV, roleIndex, userTypeIndex);
      return {
        archetype: "portfolio",
        groups: [group],
        otherPrograms: [],
        canSwitchContext: false,
        showSystemManagement,
        defaultItemId: group.items[0]?.id ?? "user"
      };
    }

    if (isSalesRep) {
      return {
        archetype: "worklist",
        groups: [SALES_REP_NAV],
        otherPrograms: [],
        // Whoever is viewing a rep keeps their own switching rights: an admin
        // always picks contexts, and a Manager picks per their OWN type — not
        // the Sales_Rep one they adopted. Keying this on the adopted type
        // trapped Managers on the worklist (bf2d864); keying it on
        // client_type_name alone re-trapped admins, whose client_type_name is
        // undefined. Same rule as the general branch below.
        canSwitchContext: !isClientUser || clientUserPicksContext(user?.client_type_name),
        showSystemManagement: false,
        defaultItemId: "accounts"
      };
    }

    const active = PROGRAM_NAV[programIndex];
    const group = active ? filterItems(active, roleIndex, userTypeIndex) : undefined;
    const archetype: ShellArchetype =
      programIndex === Program.Index.Training ? "roster" : "portfolio";

    return {
      archetype,
      groups: group ? [group] : [],
      // Client users are pinned to one program; everyone else can hop.
      otherPrograms: isClientUser
        ? []
        : (programs ?? [])
            .filter((p: any) => p.program_index !== programIndex)
            .map((p: any) => ({
              programIndex: p.program_index,
              label:
                Program.DisplayName[p.program_index as keyof typeof Program.DisplayName] ??
                p.program_name
            })),
      // Managers reach the chooser, so they need the way back to it. Other
      // client users are pinned to the one context they were seeded with.
      canSwitchContext: !isClientUser || clientUserPicksContext(user?.client_type_name),
      showSystemManagement,
      defaultItemId: group?.items?.[0]?.id ?? "dashboard"
    };
  }, [opts?.system, roleIndex, userTypeIndex, programIndex, programs, user?.client_type_name]);
};

export default useNavConfig;
